import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { usePlayer } from '../contexts/PlayerContext';
import { useTheme } from '../contexts/ThemeContext';
export type VisualizerStyle = 'vinyl' | 'particles';
interface VisualizerProps {
  style?: VisualizerStyle;
  className?: string;
}

/**
 * Signature motion moment: a Three.js scene driven by the live AnalyserNode.
 * Spins faster and brightens with real amplitude, settles when paused.
 */
export function Visualizer({
  style = 'vinyl',
  className = ''
}: VisualizerProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const {
    currentTrack,
    isPlaying,
    getLevel
  } = usePlayer();
  const {
    accent,
    secondary,
    preset
  } = useTheme();
  const levelRef = useRef(getLevel);
  const playingRef = useRef(isPlaying);
  const artworkRef = useRef<string>('');
  const labelMaterialRef = useRef<THREE.MeshBasicMaterial | null>(null);
  levelRef.current = getLevel;
  playingRef.current = isPlaying;
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const accentColor = new THREE.Color(accent);
    const secondaryColor = new THREE.Color(secondary);
    const baseColor = new THREE.Color(preset.base);
    const discColor = baseColor.clone().lerp(new THREE.Color('#ffffff'), 0.04);
    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(host.clientWidth || 1, host.clientHeight || 1, false);
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    renderer.domElement.style.display = 'block';
    host.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 0.55, 3.1);
    camera.lookAt(0, 0, 0);
    const group = new THREE.Group();
    group.rotation.x = -0.42;
    scene.add(group);
    const disposables: Array<{
      dispose: () => void;
    }> = [];
    let points: THREE.Points | null = null;
    let glowRing: THREE.Mesh | null = null;
    let basePositions: Float32Array | null = null;
    if (style === 'vinyl') {
      const discGeo = new THREE.CircleGeometry(1, 96);
      const discMat = new THREE.MeshBasicMaterial({
        color: discColor
      });
      const disc = new THREE.Mesh(discGeo, discMat);
      group.add(disc);
      disposables.push(discGeo, discMat);
      for (let i = 0; i < 9; i += 1) {
        const r = 0.46 + i * 0.06;
        const ringGeo = new THREE.RingGeometry(r, r + 0.012, 96);
        const ringMat = new THREE.MeshBasicMaterial({
          color: i % 3 === 0 ? secondaryColor : accentColor,
          transparent: true,
          opacity: 0.14,
          side: THREE.DoubleSide
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.position.z = 0.001;
        group.add(ring);
        disposables.push(ringGeo, ringMat);
      }
      const labelGeo = new THREE.CircleGeometry(0.42, 72);
      const labelMat = new THREE.MeshBasicMaterial({
        color: accentColor
      });
      const label = new THREE.Mesh(labelGeo, labelMat);
      label.position.z = 0.004;
      group.add(label);
      labelMaterialRef.current = labelMat;
      disposables.push(labelGeo, labelMat);
      const spindleGeo = new THREE.CircleGeometry(0.035, 24);
      const spindleMat = new THREE.MeshBasicMaterial({
        color: baseColor
      });
      const spindle = new THREE.Mesh(spindleGeo, spindleMat);
      spindle.position.z = 0.006;
      group.add(spindle);
      disposables.push(spindleGeo, spindleMat);
      const glowGeo = new THREE.RingGeometry(1.02, 1.12, 96);
      const glowMat = new THREE.MeshBasicMaterial({
        color: secondaryColor,
        transparent: true,
        opacity: 0.12,
        side: THREE.DoubleSide
      });
      glowRing = new THREE.Mesh(glowGeo, glowMat);
      group.add(glowRing);
      disposables.push(glowGeo, glowMat);
    } else {
      const count = 1600;
      const positions = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);
      const gold = accentColor.clone();
      const iris = secondaryColor.clone();
      for (let i = 0; i < count; i += 1) {
        const radius = 0.35 + Math.random() * 1.15;
        const angle = Math.random() * Math.PI * 2;
        positions[i * 3] = Math.cos(angle) * radius;
        positions[i * 3 + 1] = Math.sin(angle) * radius;
        positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
        const mix = gold.clone().lerp(iris, Math.random());
        colors[i * 3] = mix.r;
        colors[i * 3 + 1] = mix.g;
        colors[i * 3 + 2] = mix.b;
      }
      basePositions = positions.slice();
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      const mat = new THREE.PointsMaterial({
        size: 0.035,
        vertexColors: true,
        transparent: true,
        opacity: 0.85,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });
      points = new THREE.Points(geo, mat);
      group.add(points);
      disposables.push(geo, mat);
    }
    const resize = () => {
      const w = host.clientWidth || 1;
      const h = host.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    let frame = 0;
    let smoothed = 0;
    let spin = 0;
    const clock = new THREE.Clock();
    const tick = () => {
      frame = requestAnimationFrame(tick);
      const dt = Math.min(clock.getDelta(), 0.05);
      const raw = playingRef.current ? levelRef.current() : 0;
      smoothed += (raw - smoothed) * 0.12;
      const speed = (playingRef.current ? 0.55 : 0.05) + smoothed * 2.6;
      spin += dt * speed;
      group.rotation.z = -spin;
      group.rotation.x = -0.42 + smoothed * 0.06;
      if (glowRing) {
        const mat = glowRing.material as THREE.MeshBasicMaterial;
        mat.opacity = 0.1 + smoothed * 0.75;
        glowRing.scale.setScalar(1 + smoothed * 0.09);
      }
      if (points && basePositions) {
        const attr = points.geometry.getAttribute('position') as THREE.BufferAttribute;
        const arr = attr.array as Float32Array;
        const t = clock.elapsedTime;
        for (let i = 0; i < arr.length; i += 3) {
          const pulse = 1 + smoothed * 0.55 * Math.sin(t * 1.4 + i * 0.015);
          arr[i] = basePositions[i] * pulse;
          arr[i + 1] = basePositions[i + 1] * pulse;
          arr[i + 2] = basePositions[i + 2] + Math.sin(t + i * 0.02) * smoothed * 0.2;
        }
        attr.needsUpdate = true;
        (points.material as THREE.PointsMaterial).opacity = 0.5 + smoothed * 0.5;
      }
      renderer.render(scene, camera);
    };
    tick();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      disposables.forEach(d => d.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === host) host.removeChild(renderer.domElement);
      labelMaterialRef.current = null;
      artworkRef.current = '';
    };
  }, [accent, preset.base, secondary, style]);

  // Texture the record label with the real album artwork.
  useEffect(() => {
    const material = labelMaterialRef.current;
    const url = currentTrack?.artwork ?? '';
    if (!material || url === artworkRef.current) return;
    artworkRef.current = url;
    if (!url) {
      material.map = null;
      material.color.set(accent);
      material.needsUpdate = true;
      return;
    }
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(url, texture => {
      texture.colorSpace = THREE.SRGBColorSpace;
      material.map = texture;
      material.color.set(0xffffff);
      material.needsUpdate = true;
    }, undefined, () => {
      material.map = null;
      material.color.set(accent);
      material.needsUpdate = true;
    });
  }, [accent, currentTrack?.artwork, style]);
  return <div ref={hostRef} aria-hidden="true" className={className} data-id="element-253" data-mp="1ly7oj5irxykk" />;
}